import React, { createContext, useContext, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { getAllDemoRoutes } from '@/utils/demoRegistry';

interface DemoModeContextType {
  isDemoMode: boolean;
  preventLiveCall: (operation: string) => void;
  isDemoRoute: (path: string) => boolean;
}

const DemoModeContext = createContext<DemoModeContextType>({
  isDemoMode: false,
  preventLiveCall: () => {},
  isDemoRoute: () => false
});

export const useDemoMode = () => useContext(DemoModeContext);

interface DemoModeProviderProps {
  children: ReactNode;
}

export const DemoModeProvider: React.FC<DemoModeProviderProps> = ({ children }) => {
  const location = useLocation();
  
  // Generate demo routes dynamically from products with demos
  const dynamicDemoRoutes = getAllDemoRoutes();
  
  const demoRoutes = [
    ...dynamicDemoRoutes,
    '/products/facilities-management-suite',
    '/products/facilities-management-suite/demo',
    '/products/marketing-sales-suite',
    '/products/complete-payroll-system',
    '/admin',
    '/marketing',
    '/marketing-demo'
  ];
  
  const isDemoRoute = (path: string) => {
    return demoRoutes.some(route => path.startsWith(route));
  };
  
  const isDemoMode = isDemoRoute(location.pathname);
  
  const preventLiveCall = (operation: string) => {
    if (isDemoMode) {
      console.warn(`Demo Mode: Blocked live operation - ${operation}`);
      return true;
    }
    return false;
  };

  const contextValue: DemoModeContextType = {
    isDemoMode,
    preventLiveCall,
    isDemoRoute
  };

  // Log demo mode status for debugging
  if (isDemoMode) {
    console.log(`Demo Mode Active: ${location.pathname}`);
  }

  return (
    <DemoModeContext.Provider value={contextValue}>
      {children}
    </DemoModeContext.Provider>
  );
};

// Hook to safely make database calls only in live mode
export const useSafeDataFetch = () => {
  const { isDemoMode, preventLiveCall } = useDemoMode();
  
  return {
    isDemoMode,
    safeSupabaseCall: async function<T>(
      operation: string,
      supabaseCall: () => Promise<T>,
      fallbackData?: T
    ): Promise<T | undefined> {
      if (isDemoMode) {
        preventLiveCall(operation);
        return fallbackData;
      }
      
      try {
        return await supabaseCall();
      } catch (error) {
        console.error(`Error in ${operation}:`, error);
        return fallbackData;
      }
    }
  };
};